/**
 * What the Security board renders today.
 *
 * Proofpoint TAP authenticates with HTTP Basic and nothing else, so it needs
 * *both* halves of one credential: the service principal is the username and the
 * secret is the password. One without the other cannot authenticate at all,
 * which is why this page reports them separately rather than as a single
 * "Proofpoint not connected".
 *
 * Both values already exist in Key Vault — they are simply not injected into the
 * backend's environment — and both are currently tagged for rotation. Rotation
 * replaces them under the same secret names, so nothing in NOVA has to change
 * when it happens.
 *
 * This is not a shrug. It names each missing piece, says exactly where it comes
 * from, and shows what the board will render once it arrives — while being
 * explicit that nothing on this page is simulated.
 */

import {
  Activity,
  Building2,
  CircleCheck,
  Icon,
  KeyRound,
  type LucideIcon,
  MailWarning,
  MousePointerClick,
  ShieldCheck,
  Siren,
  TriangleAlert,
  Users,
} from "@norican/icons";
import { Alert, Badge, Card, EmptyState, Separator, Typography } from "@norican/react";
import type { ReactNode } from "react";

import type { SecurityOverview } from "@/lib/api";

/** The Key Vault the two secrets already live in. */
const KEY_VAULT = "nova-kv-dev-6636";

interface RequirementProps {
  /** The environment variable the backend reads. */
  variable: string;
  /** What the value is. */
  title: string;
  /** Whether the backend can currently see it. */
  present: boolean;
  /** Icon for the requirement row. */
  icon: LucideIcon;
  /** Where it comes from — the part that makes this page actionable. */
  children: ReactNode;
}

function Requirement({ variable, title, present, icon, children }: RequirementProps) {
  return (
    <div className="flex gap-4">
      <div className="pt-0.5">
        <Icon
          icon={present ? CircleCheck : icon}
          size="md"
          tone={present ? "success" : "warning"}
        />
      </div>
      <div className="flex flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <Typography variant="label" as="span">
            {title}
          </Typography>
          <code className="text-fg-muted bg-bg-muted rounded-sm px-1.5 py-0.5 font-mono text-xs">
            {variable}
          </code>
          <Badge tone={present ? "success" : "warning"} variant="subtle" size="sm">
            {present ? "Detected" : "Missing"}
          </Badge>
        </div>
        <Typography variant="body-sm" tone="muted">
          {children}
        </Typography>
      </div>
    </div>
  );
}

/** One line in the "what this board will show" preview. */
const PLANNED_PANELS: readonly { icon: LucideIcon; label: string; detail: string }[] = [
  {
    icon: ShieldCheck,
    label: "Dangerous emails blocked before delivery",
    detail: "The deck's headline, with its definition pinned beside it",
  },
  {
    icon: MousePointerClick,
    label: "Incidents with business impact",
    detail: "Delivered threats plus permitted clicks — danger-toned above zero",
  },
  {
    icon: Users,
    label: "People individually targeted",
    detail: "The count for everyone, the names for the security role",
  },
  {
    icon: MailWarning,
    label: "Block rate with coverage",
    detail: "The ratio, and how much of the window it was computed from",
  },
  {
    icon: Siren,
    label: "Attributed campaigns",
    detail: "Actors, malware families and volume — attackers, not employees",
  },
  {
    icon: Activity,
    label: "Mail-flow volume",
    detail: "Availability signal — alerts on a floor, not a spike",
  },
  {
    icon: Building2,
    label: "The 2026 plan",
    detail: "Training, network and SOC status — reported by their owners, not measured",
  },
];

interface NotConfiguredProps {
  /** The API's own account of what it can and cannot see. */
  overview: SecurityOverview;
}

export function NotConfigured({ overview }: NotConfiguredProps) {
  const principalPresent = overview.service_principal_present;
  const secretPresent = overview.secret_present;

  return (
    <div className="flex flex-col gap-4">
      <Card variant="outline" accent="left" accentTone="warning">
        <Card.Body>
          <EmptyState
            icon={<Icon icon={ShieldCheck} size="lg" tone="warning" />}
            title="Proofpoint TAP is not connected yet"
            description="NOVA can read email-security telemetry as soon as both halves of the credential below reach the backend. Until then this board shows nothing rather than something invented."
          />
        </Card.Body>

        <Separator />

        <Card.Body>
          <div className="flex flex-col gap-5">
            <Requirement
              variable="PROOFPOINT_SERVICE_PRINCIPAL"
              title="TAP service principal"
              present={principalPresent}
              icon={KeyRound}
            >
              TAP authenticates with HTTP Basic over TLS — no token exchange, no expiry, no refresh
              — so the service principal is the <em>username</em> half of the credential and the
              secret alone cannot authenticate. It already exists in Key Vault{" "}
              <span className="font-mono">{KEY_VAULT}</span> as{" "}
              <span className="font-mono">proofpoint-service-principal</span>, but is not injected
              into the backend&rsquo;s environment.
            </Requirement>

            <Requirement
              variable="PROOFPOINT_SECRET"
              title="TAP secret"
              present={secretPresent}
              icon={KeyRound}
            >
              The <em>password</em> half of the same credential, in Key Vault{" "}
              <span className="font-mono">{KEY_VAULT}</span> as{" "}
              <span className="font-mono">proofpoint-secret</span>. Proofpoint throttles per
              credential, so this pair is also the rate-limit identity — which is why NOVA polls one
              combined endpoint every five minutes rather than five endpoints, and why it never logs
              either value.
            </Requirement>
          </div>
        </Card.Body>

        <Card.Footer>
          <div className="flex flex-col gap-3">
            <Alert
              tone="warning"
              icon={<Icon icon={TriangleAlert} size="sm" />}
              title="Both secrets are pending rotation"
            >
              The values currently in Key Vault are tagged{" "}
              <span className="font-mono">status=compromised-rotate-immediately</span> — they were
              exposed and may still authenticate. Rotate first, then inject; do not wire the
              existing values in to get this board lit up. Rotation replaces them under the same
              secret names, so no code or configuration change is needed afterwards.
            </Alert>

            {overview.status_detail && (
              <Alert
                tone={principalPresent || secretPresent ? "warning" : "info"}
                title="What the backend reports"
              >
                {overview.status_detail}
              </Alert>
            )}
          </div>
        </Card.Footer>
      </Card>

      <Card variant="subtle">
        <Card.Body>
          <Typography variant="label" as="p" className="mb-3">
            What appears once it is connected
          </Typography>
          <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 xl:grid-cols-3">
            {PLANNED_PANELS.map((panel) => (
              <div key={panel.label} className="flex items-start gap-3">
                <Icon icon={panel.icon} size="sm" className="mt-0.5" />
                <div className="flex flex-col">
                  <Typography variant="body-sm">{panel.label}</Typography>
                  <Typography variant="caption" tone="muted">
                    {panel.detail}
                  </Typography>
                </div>
              </div>
            ))}
          </div>
          <Separator className="my-4" />
          <Typography variant="caption" tone="muted">
            What will <em>not</em> appear, by design: no recipient, sender, subject, IP address,
            URL, user agent or message identifier for bulk mail. The poller reduces every message
            response to per-day counts before the first write, and rollups are deleted after{" "}
            {overview.retention_days} days. Named Very Attacked People <em>are</em> shown, because
            the security team cannot protect a targeted individual without knowing who they are —
            role-gated to the security role, with a bounded retention window, and on this board
            only. Everyone else sees the count.
          </Typography>
        </Card.Body>
      </Card>
    </div>
  );
}
